/** @jsx dom */
import {Arrange, ArrangeFill, ArrangeFit} from 'deku-arrange';
import dom from 'magic-virtual-element';
import queryString from 'query-string';
import {Cell, Grid} from 'viskan-deku-grid';
import {setTitle} from '~/actions';
import {trackPurchase as trackPurchaseOnGTM} from '~/analytics';
import EmbeddedSnippet from '~/checkout/components/embedded/snippet';
import {getType} from '~/checkout/components/embedded';
import {Image} from '~/media';
import ReceiptCustomer from './customer';
import ReceiptRow from './row';
import ReceiptSubscriptionRow from './subscription-row';

const name = 'Receipt';

const propTypes = {
	getPrice: {
		source: 'getPrice'
	}
};

const getMediaFilename = row => {
	if (!row.meta.imageFileName) {
		return null;
	}

	return row.meta.imageFileName;
};

const getRows = (rows, {articleUrlPrefix}) => rows.map(row => <ReceiptRow key={row.rowId} mediaFilename={getMediaFilename(row)} data={row} articleUrlPrefix={articleUrlPrefix}/>);
const getSubscriptions = subscriptions => subscriptions.map(subscription => <ReceiptSubscriptionRow key={subscription.rowId} mediaFilename={getMediaFilename(subscription)} data={subscription}/>);

const getHeadOffer = (offers, getTranslation) => offers.map(offer => {
	return (
		<Arrange key={offer.offerId}>
			<ArrangeFill>
				<strong>{getTranslation('article.offer')}</strong>
			</ArrangeFill>
			<ArrangeFit>
				<div class='u-textNoWrap' innerHTML={offer.camp_name}/>
			</ArrangeFit>
		</Arrange>
	);
});

const getDiscount = (discount, discount_exvat, {getPrice, getTranslation}) => {
	return (
		<div class='Receipt-discount'>
			<Arrange>
				<ArrangeFill>
					<strong>{getTranslation('global.discount')}</strong>
				</ArrangeFill>
				<ArrangeFit>
					{getPrice(discount, discount_exvat)}
				</ArrangeFit>
			</Arrange>
		</div>
	);
};

const getReceipt = props => {
	const {getPrice, getTranslation, receipt} = props;
	const {customer, rows, subscriptions, offers, value} = receipt;
	const {total, totalExcludingTax, discountTotal, discountTotalExcludingTax, shippingFee, shippingFeeExcludingTax, paymentFee, paymentFeeExcludingTax} = value;

	return (
		<Grid gutter>
			<Cell size='full' lg-size='1of3'>
				<ReceiptCustomer customer={customer} getTranslation={getTranslation}/>
				<h5>
					{getTranslation('receipt.shipping_header')}
				</h5>
				{getTranslation('receipt.shipping')}
			</Cell>
			<Cell size='full' md-size='2of3'>
				<div class='RecieptContainerRight'>
					<div class='Receipt-rows'>
						{Boolean(rows.length) && getRows(rows, props)}
					</div>
					<div class='Receipt-rows'>
						{Boolean(subscriptions.length) && getSubscriptions(subscriptions)}
					</div>
					<div class='Receipt-summary-container'>
						<Grid>
							<Cell sm-size='hidden' md-size='1of2'>
								<noscript/>
							</Cell>
							<Cell sm-size='full' md-size='1of2'>
								<div class='Receipt-summary'>
									<div class='Receipt-offer-head'>
										{Boolean(offers.length) && getHeadOffer(offers, getTranslation)}
									</div>
									<Arrange>
										<ArrangeFill>
											<div class='Reciept-shipping-carriage'>{getTranslation('shopcart.shipping_carriage')}</div>
										</ArrangeFill>
										<ArrangeFit>
											{getPrice(shippingFee, shippingFeeExcludingTax) > 0 && getPrice(shippingFee, shippingFeeExcludingTax)}
										</ArrangeFit>
									</Arrange>
									{Boolean(discountTotal) && getDiscount(discountTotal, discountTotalExcludingTax, props)}
									{getPrice(paymentFee, paymentFeeExcludingTax) > 0 &&
									<Arrange>
										<ArrangeFill>
											<strong>{getTranslation('shopcart.payment_carriage')}</strong>
										</ArrangeFill>
										<ArrangeFit>
											{getPrice(paymentFee, paymentFeeExcludingTax)}
										</ArrangeFit>
									</Arrange>
									}
									<Arrange>
										<ArrangeFill>
											<strong>{getTranslation('global.total')}</strong>
										</ArrangeFill>
										<ArrangeFit>
											{getPrice(total, totalExcludingTax)}
										</ArrangeFit>
									</Arrange>
								</div>
							</Cell>
						</Grid>
					</div>
				</div>
			</Cell>
		</Grid>
	);
};

const getEmbeddedReceipt = (type, params) => (
	<EmbeddedSnippet type={type} params={params}/>
);

const isPurchase = props => {
	const {receipt} = props;

	if (!receipt) {
		return;
	}

	if (receipt.viewCount === 0) {
		return true;
	}

	return false;
};

const trackPurchase = (props, setState) => {
	props.fetchShopcart(true);

	trackPurchaseOnGTM(props.receipt);
	setState({trackedPurchase: true});
};

const removeDibsQuery = router => {
	const queryObject = queryString.parse(router.querystring);

	if (queryObject && queryObject.paymentid) {
		delete queryObject.paymentid;

		const path = Object.keys(queryObject).length > 0 ? window.location.pathname + '?' + queryString.stringify(queryObject) : window.location.pathname;
		window.history.replaceState({}, document.title, path);
	}
};

const beforeMount = ({props}) => {
	const {getTranslation, fetch, purchaseNumber} = props;

	if (props.router.querystring.length > 0) {
		removeDibsQuery(props.router);
	}

	setTitle(getTranslation('receipt.title'));

	fetch({
		purchaseNumber,
		mapping: 'StockKeepingUnitMetaData',
		metaMappingForSubscriptions: 'SubscriptionMetaData'
	});
};

const afterUpdate = ({props, state}, prevProps, prevState, setState) => {
	if (!props.receipt || state.trackedPurchase) {
		return;
	}

	if ('trackReceipt' in props.receipt) {
		if (props.receipt.trackReceipt) {
			trackPurchase(props, setState);
		}

		return;
	}

	if (isPurchase(props)) {
		trackPurchase(props, setState);
	}
};

const render = ({props}) => {
	const {getTranslation, receipt} = props;

	if (!receipt) {
		return <noscript/>;
	}

	const {params} = receipt;
	const type = getType(params);
	const receiptContent = type ? getEmbeddedReceipt(type, params) : getReceipt(props);
	const custname = receipt.customer.firstName;

	return (
		<div class={name}>
			<div class='RecieptContainer'>
				{!type &&
					<h2 class='Reciept-heading u-marginBottom'>{getTranslation('receipt.title', {custname})}</h2>
				}
				<div class='RecieptHeaderImage'>
					<figure>
						<Image source='succes_page.png' height='300' width='100%' alt=''/>
					</figure>
				</div>
				<div class='RecieptHeaderText'>
					{getTranslation('receipt.recieptheadertext')}
				</div>
				<div class='RecieptHeaderText2'>
					{getTranslation('receipt.recieptheadertext2')}
				</div>
				<div class='RecieptContent'>
					{receiptContent}
				</div>
			</div>
		</div>
	);
};

export default {afterUpdate, beforeMount, name, propTypes, render};
